import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Box, Typography, Button, ButtonGroup, Chip } from '@mui/material';
import { PlayArrow, Pause, SkipNext, SkipPrevious } from '@mui/icons-material';
import ComparisonVideoSession from './ComparisonVideoSession';
import MasterScrubber from './MasterScrubber';
import MasterControls from './MasterControls';
import { useVideoSession } from './hooks/useVideoSession';
import { useSyncManager } from './hooks/useSyncManager';
import { ArrowBack } from '@mui/icons-material';
import { IconButton } from '@mui/material';

/**
 * SessionComparison - Side-by-side video comparison component
 * Manages two video sessions with synchronization controls
 */
function SessionComparison({
  videoIdA,
  videoIdB,
  sessionMetadata = null,
  className = '',
  liftCreatingSession,
  liftSelectedSession,
}) {
  // Load session data for both videos
  const sessionA = useVideoSession(videoIdA);
  const sessionB = useVideoSession(videoIdB);

  // Master skip seconds state
  const [skipSeconds, setSkipSeconds] = useState(5);

  const handleBack = () => {
    liftSelectedSession(null);
    liftCreatingSession(0); // Navigate back to event list
  };
  // Prepare sessions for sync manager
  const sessions = useMemo(
    () =>
      [
        { videoId: videoIdA, sessionData: sessionA.sessionData },
        { videoId: videoIdB, sessionData: sessionB.sessionData },
      ].filter((s) => s.sessionData),
    [videoIdA, videoIdB, sessionA.sessionData, sessionB.sessionData]
  );

  // Sync management
  const sync = useSyncManager(sessions);

  // Use first video as reference for timing calculations
  const referenceVideoId = videoIdA;

  // Get current sync state for both videos
  const currentSyncState = useMemo(
    () => sync.getCurrentSyncState(referenceVideoId),
    [sync, referenceVideoId]
  );
  const videoTimeA = currentSyncState.videoTimes.get(videoIdA);
  const videoTimeB = currentSyncState.videoTimes.get(videoIdB);

  // Handle timeline controls
  const handleTimelinePlay = useCallback(() => {
    sync.setTimelineState({ isPlaying: true });
    sync.resetAllToSync(); // Reset all videos to sync mode
  }, [sync]);

  const handleTimelinePause = useCallback(() => {
    sync.setTimelineState({ isPlaying: false });
  }, [sync]);

  const handleTimelineSeek = useCallback(
    (newTime) => {
      sync.setTimelineState({ currentTime: newTime });
      sync.resetAllToSync(); // Reset all videos to sync mode when seeking
    },
    [sync]
  );

  // No more video-driven time updates! Timeline is now independent.

  // Handle lap selection
  const handleLapSelection = useCallback(
    (videoId, lapNumber) => {
      // Update the sync manager with the new lap selection
      sync.handleLapSelection(videoId, lapNumber);

      // Calculate the lap start time for the reference video after the selection
      const referenceLapStartTime =
        sync.syncManagerRef?.current?.getLapStartTime(referenceVideoId) || 0;

      // Set timeline to 10,000 (our baseline) when jumping to lap starts
      // The sync calculation will position each video at their respective lap start times
      sync.setTimelineState({
        currentTime: 10000,
        isPlaying: false,
      });

      // Reset all videos to sync mode
      sync.resetAllToSync();
    },
    [sync, referenceVideoId]
  );

  // Handle individual video controls (nudging)
  const handleIndividualPlay = useCallback(
    (videoId) => {
      // Switch video to individual mode - it will handle its own playback
      sync.setVideoMode(videoId, 'individual');
    },
    [sync]
  );

  const handleIndividualPause = useCallback(
    (videoId) => {
      // Switch video to individual mode - it will handle its own playback
      sync.setVideoMode(videoId, 'individual');
    },
    [sync]
  );

  const handleIndividualSeek = useCallback(
    (videoId, newTime) => {
      // Individual seek switches to custom sync mode
      sync.handleVideoTimeChange(videoId, newTime, referenceVideoId);
    },
    [sync, referenceVideoId]
  );

  // Handle sync mode toggle
  const handleSyncModeToggle = useCallback(() => {
    const newMode = sync.syncMode === 'lapStart' ? 'custom' : 'lapStart';
    sync.setSyncMode(newMode);
  }, [sync]);

  // Skip controls - now using dynamic skipSeconds
  const handleSkipBackward = useCallback(
    (seconds = skipSeconds) => {
      const newTime = Math.max(0, sync.timeline.currentTime - seconds);
      handleTimelineSeek(newTime);
    },
    [sync.timeline.currentTime, handleTimelineSeek, skipSeconds]
  );

  const handleSkipForward = useCallback(
    (seconds = skipSeconds) => {
      const newTime = sync.timeline.currentTime + seconds;
      handleTimelineSeek(newTime);
    },
    [sync.timeline.currentTime, handleTimelineSeek, skipSeconds]
  );

  // Auto-pause when lap selection changes - removed to prevent infinite loops
  // This will be handled in the lap selection handler instead

  // Display loading state
  if (sessionA.isLoading || sessionB.isLoading) {
    return (
      <Box className={className} sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Session Comparison
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Loading session data...
        </Typography>
      </Box>
    );
  }

  // Display error state
  if (sessionA.error || sessionB.error) {
    return (
      <Box className={className} sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Session Comparison - Error
        </Typography>
        {sessionA.error && (
          <Typography variant="body2" color="error" gutterBottom>
            Video A: {sessionA.error.message}
          </Typography>
        )}
        {sessionB.error && (
          <Typography variant="body2" color="error">
            Video B: {sessionB.error.message}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box
      className={className}
      sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={handleBack} size="small">
          <ArrowBack />
        </IconButton>

        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {sessionMetadata?.eventVenue || 'Unknown Track'}
          {sessionMetadata?.trackConfig && ` - ${sessionMetadata.trackConfig}`}
          {/* {sessionMetadata?.eventName && ` • ${sessionMetadata.eventName}`} */}
        </Typography>
      </Box>
      {/* Video Comparison with Center Controls */}
      <Box
        sx={{
          display: 'flex',
          flexGrow: 1,
          gap: 2,
          p: 0,
          minHeight: 0,
        }}
      >
        {/* Video A */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ComparisonVideoSession
            videoId={videoIdA}
            sessionData={sessionA.sessionData}
            sessionMetadata={sessionMetadata}
            isLoading={sessionA.isLoading}
            error={sessionA.error}
            label="Video A"
            currentTime={videoTimeA?.currentTime || 0}
            isPlaying={
              sync.videoModes.get(videoIdA) === 'sync'
                ? sync.timeline.isPlaying
                : undefined
            }
            playbackRate={sync.timeline.playbackRate}
            videoMode={sync.videoModes.get(videoIdA) || 'sync'}
            selectedLap={sync.getSelectedLap(videoIdA)}
            onLapSelection={handleLapSelection}
            onIndividualPlay={handleIndividualPlay}
            onIndividualPause={handleIndividualPause}
            onIndividualSeek={handleIndividualSeek}
          />
        </Box>

        {/* Video B */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ComparisonVideoSession
            videoId={videoIdB}
            sessionData={sessionB.sessionData}
            sessionMetadata={sessionMetadata}
            isLoading={sessionB.isLoading}
            error={sessionB.error}
            label="Video B"
            currentTime={videoTimeB?.currentTime || 0}
            isPlaying={
              sync.videoModes.get(videoIdB) === 'sync'
                ? sync.timeline.isPlaying
                : undefined
            }
            playbackRate={sync.timeline.playbackRate}
            videoMode={sync.videoModes.get(videoIdB) || 'sync'}
            selectedLap={sync.getSelectedLap(videoIdB)}
            onLapSelection={handleLapSelection}
            onIndividualPlay={handleIndividualPlay}
            onIndividualPause={handleIndividualPause}
            onIndividualSeek={handleIndividualSeek}
          />
        </Box>
      </Box>
      <Box>
        {/* Center Master Controls */}
        <MasterControls
          syncMode={sync.syncMode}
          isPlaying={sync.timeline.isPlaying}
          playbackRate={sync.timeline.playbackRate}
          skipSeconds={skipSeconds}
          onSyncModeToggle={handleSyncModeToggle}
          onPlay={handleTimelinePlay}
          onPause={handleTimelinePause}
          onSkipBackward={handleSkipBackward}
          onSkipForward={handleSkipForward}
          onSetSkipSeconds={setSkipSeconds}
        />
      </Box>
      {/* Simplified Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'grey.50',
        }}
      >
        {/* Master Scrubber */}
        <MasterScrubber
          timeline={sync.timeline}
          sessions={sessions}
          selectedLaps={
            new Map([
              [videoIdA, sync.getSelectedLap(videoIdA)],
              [videoIdB, sync.getSelectedLap(videoIdB)],
            ])
          }
          onSeek={handleTimelineSeek}
        />
      </Box>
      {/* Debug Info (temporary) */}
      {process.env.NODE_ENV === 'development' && (
        <Box
          sx={{
            p: 1,
            backgroundColor: 'grey.100',
            borderTop: '1px solid',
            borderColor: 'divider',
            fontSize: '0.7rem',
          }}
        >
          <Typography variant="caption">
            Debug: Mode={sync.syncMode} | Timeline=
            {sync.timeline.currentTime.toFixed(1)} | A_Time=
            {videoTimeA?.currentTime?.toFixed(1)} (
            {sync.videoModes.get(videoIdA)}, Lap {sync.getSelectedLap(videoIdA)}
            , Offset {sync.getCustomOffset(videoIdA).toFixed(1)}) | B_Time=
            {videoTimeB?.currentTime?.toFixed(1)} (
            {sync.videoModes.get(videoIdB)}, Lap {sync.getSelectedLap(videoIdB)}
            , Offset {sync.getCustomOffset(videoIdB).toFixed(1)})
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default SessionComparison;
